import {Box, createStyles, Flex, Text, Divider} from '@mantine/core';
import React, {useCallback, useMemo, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import useAntibodyUIState from 'antibodies/hooks/useAntibodyUIState';
import {Permissions} from 'auth/PermissionConstants';
import {ANTIBODIES_ANALYTICS_ACTIONS} from 'constants/analytics-products/AntibodiesAnalyticsConstants';
import {QueryResultType, useSkippableBenchlingQuery, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {areAllAccessible, isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  requireScientificModel,
  type ScientificModelNode,
  type ScientificModelsByExternalReference,
} from 'model-hub/scientific-models/ScientificModelMetadata';
import {InProgressPredictionData} from 'model-hub/scientific-models/structure-prediction/CoordinateStructureList';
import StructurePredictionTab from 'model-hub/scientific-models/structure-prediction/StructurePredictionTab';
import {ViewState} from 'model-hub/scientific-models/structure-prediction/structurePredictionViewState';
import StructureViewerHeader from 'model-hub/scientific-models/structure-prediction/StructureViewerHeader';
import {StructureViewerPane} from 'model-hub/scientific-models/structure-prediction/StructureViewerPane';
import {useScientificModelRegistry} from 'model-hub/scientific-models/useAvailableScientificModels';
import {CenteredSpinner} from 'ui-library/Spinner';

import {
  ProteinEntityStructurePredictionQuery,
  ProteinEntityStructurePredictionQueryVariables,
} from './__generated/ProteinEntityStructurePredictionQuery';
import type {
  ProteinPredictionJobsQuery,
  ProteinPredictionJobsQueryVariables,
} from './__generated/ProteinPredictionJobsQuery';
import {PROTEIN_ENTITY_STRUCTURE_PREDICTION_QUERY} from './ProteinEntityStructurePredictionQuery';
import {
  getCompletedStructuresFromJobs,
  TERMINAL_PREDICTION_JOB_STATUSES,
} from './proteinEntityStructureUtils';
import {PROTEIN_PREDICTION_JOBS_QUERY} from './ProteinPredictionJobsQuery';

const useStyles = createStyles(() => ({
  divider: {
    marginTop: 0,
    // Match the spacing to the aa sequence tab which renders a separate react tree for options as well as the
    // molstar viewer.
    marginBottom: 3,
  },
  // Take only the height left over from the header and divider so the viewer pane (which is h="100%")
  // resolves against the remaining space instead of the full container and overflows off the bottom.
  viewerContainer: {
    flex: '1 1 auto',
    minHeight: 0,
  },
}));

export interface ProteinEntityStructureTabViewProps {
  proteinId: string;
}

export default function ProteinEntityStructureTabView({
  proteinId,
}: ProteinEntityStructureTabViewProps): JSX.Element {
  const scientificModelRegistryResult = useScientificModelRegistry();

  const {
    state: {openVersionId},
  } = useAntibodyUIState(proteinId);
  const queryResult = useSkippableBenchlingQuery<
    ProteinEntityStructurePredictionQuery,
    ProteinEntityStructurePredictionQueryVariables
  >(PROTEIN_ENTITY_STRUCTURE_PREDICTION_QUERY, {
    variables: {proteinId, versionId: openVersionId!},
    skip: openVersionId == null,
  });

  if (
    scientificModelRegistryResult.type === 'loading' ||
    openVersionId == null ||
    queryResult.type === QueryResultType.PENDING ||
    queryResult.type === QueryResultType.SKIPPED
  ) {
    return <CenteredSpinner />;
  }
  if (queryResult.type === QueryResultType.ERROR) {
    return (
      <Text p="lg" size="sm">
        Error when loading structure prediction details. Please refresh the page.
      </Text>
    );
  }

  if (scientificModelRegistryResult.type === 'error') {
    return (
      <Text p="lg" size="sm">
        Error when loading scientific models. Please refresh the page.
      </Text>
    );
  }

  const {bioentity, antibody: protein} = queryResult.data;
  if (isInaccessible(bioentity) || isInaccessible(protein)) {
    return (
      <Text p="lg" size="sm">
        This protein entity is not accessible for structure prediction.
      </Text>
    );
  }

  if (bioentity.__typename !== 'BasicFolderItemNode') {
    return (
      <Text p="lg" size="sm">
        This entity does not support structure prediction.
      </Text>
    );
  }

  const proteinVersion = protein.version;
  if (proteinVersion == null) {
    return (
      <Text p="lg" size="sm">
        No protein version is available for structure prediction.
      </Text>
    );
  }

  const chainEntities = proteinVersion.chains.map((chain) => chain.entity);
  if (!areAllAccessible(chainEntities)) {
    return (
      <Text p="lg" size="sm">
        Components of this protein entity are not accessible for structure prediction.
      </Text>
    );
  }

  // Extract chain sequences, respecting multiplicity.
  const chains = chainEntities.flatMap((entity, index) =>
    proteinVersion.chains[index].refs.map(() => entity.experimentalCurrentVersion.aminoAcids)
  );
  const inputPermissions: Permissions = {
    admin: false,
    appendable: false,
    writable: false,
    readable: chainEntities.every((entity) => entity.permissions.readable),
    structurePredictionEditable: chainEntities.every((entity) =>
      Boolean(entity.permissions.structurePredictionEditable)
    ),
  };

  return (
    <LoadedProteinEntityStructureTabView
      proteinId={proteinId}
      chains={chains}
      versionId={proteinVersion.id}
      inputPermissions={inputPermissions}
      folderId={bioentity.folder.id}
      availableModels={scientificModelRegistryResult.registry.availableStructurePredictionModels}
      catalogByExternalReference={scientificModelRegistryResult.registry.catalogByExternalReference}
    />
  );
}

interface LoadedProteinEntityStructureTabViewProps {
  proteinId: string;
  chains: Array<string>;
  versionId: string;
  inputPermissions: Permissions;
  folderId: string;
  availableModels: ReadonlyArray<ScientificModelNode>;
  catalogByExternalReference: ScientificModelsByExternalReference;
}

function LoadedProteinEntityStructureTabView({
  proteinId,
  chains,
  versionId,
  inputPermissions,
  folderId,
  availableModels,
  catalogByExternalReference,
}: LoadedProteinEntityStructureTabViewProps): JSX.Element {
  const {classes} = useStyles();

  const multimerCapableModels = useMemo(
    () => availableModels.filter((model) => model.capabilities.supportsMultimers),
    [availableModels]
  );

  const proteinPredictionJobsResult = useBenchlingQuery<
    ProteinPredictionJobsQuery,
    ProteinPredictionJobsQueryVariables
  >(PROTEIN_PREDICTION_JOBS_QUERY, {
    variables: {proteinVersionId: versionId},
    // Keep rendering the current job list while a new-run submission refetches in the background,
    // so the in-progress list doesn't flash empty and re-populate as the refetch resolves.
    renderCachedDataWhenRefetching: true,
  });

  const jobs =
    proteinPredictionJobsResult.type === QueryResultType.SUCCESS
      ? proteinPredictionJobsResult.data.proteinVersionPredictionJobs
      : null;

  const inProgressStructurePredictions: Array<InProgressPredictionData> = useMemo(
    () =>
      (jobs ?? [])
        .filter((job) => !TERMINAL_PREDICTION_JOB_STATUSES.has(job.status))
        .map((job) => ({
          id: job.id,
          model: requireScientificModel(catalogByExternalReference, job.modelName),
          createdAt: job.createdAt,
          componentIds: job.componentApiIds,
        })),
    [catalogByExternalReference, jobs]
  );

  const completedStructures = useMemo(() => getCompletedStructuresFromJobs(jobs ?? []), [jobs]);

  const [selectedStructureId, setSelectedStructureId] = useState<string | null>(null);

  // Resolve against the current list so a refetch (which mints fresh signed URLs) flows through to the
  // viewer instead of leaving it pointed at an expired URL captured at selection time. With no explicit
  // selection (e.g. the tab opening straight into the viewer) default to the most recent structure.
  const selectedStructure =
    selectedStructureId == null
      ? (completedStructures[completedStructures.length - 1] ?? null)
      : (completedStructures.find((structure) => structure.coordinateStructureId === selectedStructureId) ??
        null);

  const {refetch: refetchProteinPredictionJobs} = proteinPredictionJobsResult;
  const refreshData = useCallback(() => {
    refetchProteinPredictionJobs();
  }, [refetchProteinPredictionJobs]);

  return (
    <StructurePredictionTab
      availableModels={multimerCapableModels}
      structurePredictionQueryStatus={proteinPredictionJobsResult.type}
      inProgressStructurePredictions={inProgressStructurePredictions}
      completedStructurePredictions={completedStructures}
      permissions={inputPermissions}
      catalogByExternalReference={catalogByExternalReference}
      onSelectCoordinateStructureId={setSelectedStructureId}
      onSubmitNewRun={refreshData}
      inputComponent={{id: proteinId, folderId, permissions: inputPermissions, chains}}
      versionId={versionId}
    >
      {({setViewState}) => {
        if (!selectedStructure) {
          // Fallback to loading if somehow we get into an inconsistent state
          return <CenteredSpinner />;
        }
        return (
          <Flex direction="column" h="100%">
            <StructureViewerHeader
              structureFile={selectedStructure.structureFile}
              model={requireScientificModel(catalogByExternalReference, selectedStructure.model)}
              modelVersion={selectedStructure.modelVersion}
              // Job-sourced structures already carry a signed standardized-output download URL (the same
              // one the viewer renders), so open it directly rather than re-signing an external file.
              downloadStructureFile={() => {
                window.open(selectedStructure.structureFile.url, '_blank', 'noopener noreferrer');
                trackEvent(ANTIBODIES_ANALYTICS_ACTIONS.DOWNLOADED_PROTEIN_STRUCTURE);
              }}
            />
            <Divider className={classes.divider} />
            <Box className={classes.viewerContainer}>
              <StructureViewerPane
                structureInfo={selectedStructure}
                catalogByExternalReference={catalogByExternalReference}
                predictionInProgress={inProgressStructurePredictions.length > 0}
                primaryEntityId={proteinId}
                interactionAnalyticsEvent={ANTIBODIES_ANALYTICS_ACTIONS.INTERACTED_PROTEIN_STRUCTURE_VIEWER}
                testComponent="ProteinStructureTabView"
                onBack={() => {
                  setSelectedStructureId(null);
                  setViewState(ViewState.COORDINATE_STRUCTURES_LIST);
                }}
                // Job-sourced structures already carry a signed standardized-output URL, so render it
                // directly and refetch the jobs (minting fresh URLs) once it expires rather than
                // re-signing a non-external-file job id.
                onRefreshExpiredUrl={refreshData}
                aminoAcids={chains.join('')}
                permissions={inputPermissions}
                // TODO(BNCH-172489): Handle selected sections from the protein map aa sequence maps.
              />
            </Box>
          </Flex>
        );
      }}
    </StructurePredictionTab>
  );
}
